import * as React from "react";
import { CauseTypeLike } from "./hooks/useCheckInCausesToOfferLoader";

type Cause = {
  id: string;
  causeValue?: string;
  [key: string]: any;
};

export type ExistingRecord = {
  id: string;
  uid?: string;
  duration?: number;
  mood?: string;
  timestamp?: any;
  [key: string]: any;
};

type CheckInCtx = {
  selectedCauses: Cause[];
  causesToOffer: Cause[];
  loadingCauses: boolean;
  feelingName: string | null;
  selectedDate: string | Date | null;
  existingRecord: ExistingRecord | null | undefined;

  selectCause: (cause: Cause) => void;
  clearCauses: () => void;
  setMoodLabel: (label: string | null) => void;
  setDate: (date: string | Date | null) => void;
  setCausesLoading: (v: boolean) => void;
  setCausesToOffer: (c: Cause[]) => void;
};

const CheckInContext = React.createContext<CheckInCtx | null>(null);

export function CheckInProvider({
  children,
  initialfeelingName,
  initialSelectedDate,
  initialExistingRecord,
}: {
  children: React.ReactNode;
  initialfeelingName?: string | null;
  initialSelectedDate?: string | Date | null;
  initialExistingRecord?: ExistingRecord | null;
}) {
  const [selectedCauses, setSelectedCauses] = React.useState<Cause[]>([]);
  const [causesToOffer, setCausesToOfferState] = React.useState<Cause[]>([]);
  const [loadingCauses, setLoadingCauses] = React.useState<boolean>(false);
  const [feelingName, setfeelingName] = React.useState<
    string | null | undefined
  >(initialfeelingName);
  const [selectedDate, setSelectedDate] = React.useState<
    string | Date | null | undefined
  >(initialSelectedDate);
  const [existingRecord, setExistingRecord] = React.useState<
    ExistingRecord | undefined | null
  >(initialExistingRecord);

  React.useEffect(() => {
    if (initialfeelingName !== undefined) {
      setfeelingName(initialfeelingName);
    }
  }, [initialfeelingName]);

  React.useEffect(() => {
    if (initialExistingRecord !== undefined) {
      setExistingRecord(initialExistingRecord);
    }
  }, [initialExistingRecord]);

  React.useEffect(() => {
    if (initialSelectedDate !== undefined) {
      setSelectedDate(initialSelectedDate);
    }
  }, [initialSelectedDate]);

  const selectCause = React.useCallback((cause: Cause) => {
    setSelectedCauses((prev) =>
      prev.some((c) => c.id === cause.id)
        ? prev.filter((c) => c.id !== cause.id)
        : [...prev, cause]
    );
  }, []);

  const clearCauses = React.useCallback(() => {
    setSelectedCauses([]);
  }, []);

  const setMoodLabel = React.useCallback((label: string | null) => {
    setfeelingName(label);
  }, []);

  const setDate = React.useCallback((date: string | Date | null) => {
    setSelectedDate(date);
  }, []);

  const setCausesLoading = React.useCallback((v: boolean) => {
    setLoadingCauses(v);
  }, []);

  const setCausesToOffer = React.useCallback((c: Cause[]) => {
    setCausesToOfferState(c);
  }, []);

  const value: CheckInCtx = {
    selectedCauses,
    causesToOffer,
    loadingCauses,
    feelingName: feelingName ?? null,
    selectedDate: selectedDate ?? null,
    existingRecord: existingRecord ?? null,

    selectCause,
    clearCauses,
    setMoodLabel,
    setDate,
    setCausesLoading,
    setCausesToOffer,
  };

  return (
    <CheckInContext.Provider value={value}>{children}</CheckInContext.Provider>
  );
}

export function useCheckIn() {
  const ctx = React.useContext(CheckInContext);
  if (!ctx) {
    throw new Error("useCheckIn must be used within CheckInProvider");
  }
  return ctx;
}
